<template>
  <div class="design-dropbox">
    <front-page-design-nav theme="light" :show-designs-link="false" />

    <section class="hero">
      <div class="hero__text">
        <h1>Turn visual work into business critical data</h1>
        <p class="hero__sub">
          Box21 lets you build, deploy, and monitor custom vision AI — from medication checks
          in health care to LEGO sorting, industrial inspection, and wildlife monitoring.
        </p>
        <div class="hero__actions">
          <button type="button" class="btn btn--blue" @click="goToRegister">Create free account</button>
          <button type="button" class="btn btn--text" @click="goToLogin">Sign in</button>
        </div>
      </div>
      <div class="hero__visual">
        <div class="hero__card hero__card--back" />
        <div class="hero__card hero__card--front">
          <img :src="heroImage" alt="Annotated detection example" />
          <div class="hero__badge">98% confidence</div>
        </div>
      </div>
    </section>

    <section class="capabilities">
      <p class="section-eyebrow">AI is moving from hype to operation</p>
      <h2 class="section-title">What vision AI can do for you</h2>
      <div class="capabilities__grid">
        <div class="capability">
          <img class="capability__img" src="/api/static/examples/fig-detection.jpg" alt="Detected ripe and unripe figs on a tree" />
          <h3>Understand visual data</h3>
          <p>AI can understand images, video, and physical environments — turning raw visual input into actionable data.</p>
        </div>
        <div class="capability">
          <img class="capability__img" src="/api/static/examples/anomaly-detection.jpg" alt="Anomaly detection marking parts as OK or not OK" />
          <h3>Count, detect & validate</h3>
          <p>Count parts, detect anomalies, and validate process steps automatically without manual inspection.</p>
        </div>
        <div class="capability">
          <img class="capability__img" src="/api/static/examples/parking-check.jpg" alt="Automatic parking check with license plate detection" />
          <h3>Verify manual work</h3>
          <p>Verify work that previously required hours of manual inspection — consistently and at scale.</p>
        </div>
        <div class="capability">
          <img class="capability__img" src="/api/static/examples/warehouse-robot.jpg" alt="Robot arm picking in a warehouse" />
          <h3>New automation</h3>
          <p>Enable automation opportunities that were previously too costly or impractical — now within reach.</p>
        </div>
      </div>
    </section>

    <section class="stories">
      <h2 class="section-title">Proven in production</h2>
      <p class="section-sub">From health care to consumer products to field research — the use cases are endless.</p>
      <div class="stories__list">
        <article v-for="item in showcase" :key="item.id" class="story">
          <img :src="item.image" :alt="item.title" />
          <div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.text }}</p>
          </div>
        </article>
      </div>
    </section>

    <section class="testimonial">
      <blockquote>
        <img class="testimonial__logo" src="/api/static/frontpage/tantelouise-logo.png" alt="tanteLouise" />
        <p>“{{ testimonial.quote }}”</p>
        <footer>
          <strong>{{ testimonial.name }}</strong> — {{ testimonial.role }}
          <span class="testimonial__source">Source: {{ testimonial.source }}</span>
        </footer>
      </blockquote>
    </section>

    <section class="workflow">
      <h2 class="section-title">From idea to production</h2>
      <p class="section-sub">Go from raw images to deployed models with our end-to-end platform.</p>
      <div class="workflow__steps">
        <div v-for="step in workflow" :key="step.step" class="workflow__step">
          <span class="workflow__num">{{ step.step }}</span>
          <h3>{{ step.title }}</h3>
          <p>{{ step.text }}</p>
        </div>
      </div>
    </section>

    <section class="stats">
      <div v-for="stat in stats" :key="stat.label" class="stats__item">
        <span class="stats__value">{{ stat.value }}</span>
        <span class="stats__label">{{ stat.label }}</span>
      </div>
    </section>

    <section class="industries">
      <h2 class="section-title">Vision AI across industries</h2>
      <p class="section-sub">In the digital and physical world — your imagination is the limit.</p>
      <div class="industries__grid">
        <div v-for="ind in industries" :key="ind.id" class="industries__card">
          <h3>{{ ind.title }}</h3>
          <p>{{ ind.text }}</p>
        </div>
      </div>
    </section>

    <section class="integration">
      <h2 class="section-title">Integration and focus is key</h2>
      <p class="section-sub">Box21 is not a standalone tool — it becomes part of your business process.</p>
      <div class="integration__grid">
        <div v-for="item in integration" :key="item.id" class="integration__card">
          <h3>{{ item.title }}</h3>
          <p>{{ item.text }}</p>
        </div>
      </div>
    </section>

    <section class="developer">
      <div class="developer__text">
        <h2>Designed for developers</h2>
        <p>
          Use the web platform or integrate via API. Export models in 19 formats
          and deploy to edge, mobile, or cloud — with full Python SDK control.
        </p>
      </div>
      <div class="developer__code">
        <pre><code><span class="code-comment"># Train and export a YOLO model</span>
<span class="code-keyword">from</span> box21 <span class="code-keyword">import</span> Client

client = Client(api_key=<span class="code-string">"your-api-key"</span>)
model = client.train(
    project=<span class="code-string">"lego-sorter"</span>,
    architecture=<span class="code-string">"Boxify"</span>,
)
model.export(format=<span class="code-string">"coreml"</span>)</code></pre>
      </div>
    </section>

    <section class="faq">
      <h2 class="section-title">Frequently asked questions</h2>
      <div class="faq__list">
        <details v-for="item in faq" :key="item.q" class="faq__item">
          <summary>{{ item.q }}</summary>
          <p>{{ item.a }}</p>
        </details>
      </div>
    </section>

    <section class="cta">
      <h2>What about your business?</h2>
      <p>Join teams in healthcare, industry, and research building vision AI with Box21.</p>
      <div class="cta__actions">
        <button type="button" class="btn btn--blue" @click="goToRegister">Create free account</button>
        <router-link v-if="showTeamLink" to="/team" class="btn btn--text">Meet the team</router-link>
      </div>
    </section>

    <front-page-design-footer theme="light" />
  </div>
</template>

<script>
import FrontPageDesignNav from '@/components/front_page/designs/FrontPageDesignNav.vue'
import FrontPageDesignFooter from '@/components/front_page/designs/FrontPageDesignFooter.vue'
import {
  SHOWCASE,
  HERO_IMAGE,
  WORKFLOW,
  STATS,
  INDUSTRIES,
  FAQ,
  INTEGRATION,
  TESTIMONIAL,
  SHOW_TEAM_LINK,
} from '@/components/front_page/designs/shared'

export default {
  name: 'DesignDropbox',
  components: { FrontPageDesignNav, FrontPageDesignFooter },
  data() {
    return {
      showcase: SHOWCASE,
      heroImage: HERO_IMAGE,
      workflow: WORKFLOW,
      stats: STATS,
      industries: INDUSTRIES,
      faq: FAQ,
      integration: INTEGRATION,
      testimonial: TESTIMONIAL,
      showTeamLink: SHOW_TEAM_LINK,
    }
  },
  methods: {
    goToLogin() { this.$router.push('/login') },
    goToRegister() { this.$router.push({ path: '/login', query: { tab: 'register' } }) },
  },
}
</script>

<style scoped>
.design-dropbox {
  min-height: 100vh;
  background: #fff;
  color: #1e1919;
  font-family: 'Segoe UI', system-ui, sans-serif;
}

.hero {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px 96px;
  align-items: center;
}

.hero h1 {
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  line-height: 1.15;
  margin: 0 0 20px;
  letter-spacing: -0.02em;
}

.hero__sub {
  font-size: 1.125rem;
  color: #637282;
  line-height: 1.7;
  margin: 0 0 28px;
  max-width: 480px;
}

.hero__actions {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.btn {
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  border-radius: 8px;
  padding: 14px 24px;
}

.btn--blue {
  background: #0677b9;
  color: #fff;
}

.btn--blue:hover {
  background: #055a8c;
}

.btn--text {
  background: transparent;
  color: #0677b9;
  text-decoration: underline;
}

.hero__visual {
  position: relative;
  min-height: 360px;
}

.hero__card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.12);
}

.hero__card--back {
  position: absolute;
  top: 24px;
  right: 0;
  width: 85%;
  height: 280px;
  background: linear-gradient(135deg, #7bacd6, #0677b9);
  transform: rotate(3deg);
}

.hero__card--front {
  position: relative;
  width: 90%;
  background: #f7f9fa;
}

.hero__card--front img {
  width: 100%;
  display: block;
  aspect-ratio: 16/10;
  object-fit: cover;
}

.hero__badge {
  position: absolute;
  bottom: 16px;
  right: 16px;
  background: #fff;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #0677b9;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.capabilities {
  max-width: 1100px;
  margin: 0 auto;
  padding: 64px 24px 80px;
}

.capabilities__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.capability {
  padding: 32px 28px;
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 16px;
  transition: box-shadow 0.2s ease;
}

.capability:hover {
  box-shadow: 0 8px 24px rgba(6, 119, 185, 0.08);
}

.capability__img {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 10px;
  margin-bottom: 16px;
  display: block;
}

.capability h3 {
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0 0 8px;
}

.capability p {
  color: #637282;
  line-height: 1.6;
  margin: 0;
  font-size: 0.95rem;
}

.stories {
  max-width: 1000px;
  margin: 0 auto;
  padding: 80px 24px;
}

.stories__list {
  display: grid;
  gap: 48px;
}

.story {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 40px;
  align-items: center;
}

.story img {
  width: 100%;
  aspect-ratio: 16 / 11;
  object-fit: cover;
  border-radius: 12px;
}

.story h3 {
  font-size: 1.5rem;
  margin: 0 0 12px;
}

.story p {
  color: #637282;
  line-height: 1.7;
  margin: 0;
}

.section-title {
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 700;
  text-align: center;
  margin: 0 0 12px;
  letter-spacing: -0.02em;
}

.section-sub {
  text-align: center;
  color: #637282;
  font-size: 1.125rem;
  margin: 0 0 48px;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
}

.section-eyebrow {
  text-align: center;
  color: #0677b9;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 8px;
}

.testimonial {
  background: #f7f9fa;
  padding: 72px 24px;
}

.testimonial blockquote {
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
}

.testimonial blockquote p {
  font-size: clamp(1.15rem, 2vw, 1.4rem);
  line-height: 1.6;
  font-weight: 500;
  color: #1e1919;
  margin: 0 0 24px;
}

.testimonial footer {
  color: #637282;
  font-size: 0.95rem;
}

.testimonial footer strong {
  color: #1e1919;
}

.testimonial__source {
  display: block;
  margin-top: 4px;
  font-size: 0.8rem;
  opacity: 0.8;
}

.testimonial__logo {
  width: auto;
  margin-bottom: 24px;
}

.integration {
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 24px;
}

.integration__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.integration__card {
  padding: 28px 24px;
  background: #f7f9fa;
  border-radius: 12px;
}

.integration__card h3 {
  font-size: 1.1rem;
  margin: 0 0 8px;
}

.integration__card p {
  color: #637282;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
}

.workflow {
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 24px;
}

.workflow__steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
}

.workflow__step {
  padding: 32px 24px;
  background: #f7f9fa;
  border-radius: 16px;
}

.workflow__num {
  display: inline-block;
  font-size: 0.85rem;
  font-weight: 700;
  color: #0677b9;
  background: rgba(6, 119, 185, 0.1);
  padding: 4px 10px;
  border-radius: 999px;
  margin-bottom: 16px;
}

.workflow__step h3 {
  font-size: 1.25rem;
  margin: 0 0 8px;
}

.workflow__step p {
  color: #637282;
  line-height: 1.6;
  margin: 0;
  font-size: 0.95rem;
}

.stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  max-width: 1000px;
  margin: 0 auto;
  padding: 64px 24px;
  background: #0677b9;
  border-radius: 16px;
  color: #fff;
}

.stats__item {
  text-align: center;
}

.stats__value {
  display: block;
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 700;
  margin-bottom: 4px;
}

.stats__label {
  font-size: 0.9rem;
  opacity: 0.85;
}

.industries {
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 24px;
}

.industries__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.industries__card {
  padding: 28px 24px;
  border: 1px solid #e8ecf0;
  border-radius: 12px;
  transition: box-shadow 0.2s ease;
}

.industries__card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
}

.industries__card h3 {
  font-size: 1.1rem;
  margin: 0 0 8px;
}

.industries__card p {
  color: #637282;
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0;
}

.developer {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 24px;
  align-items: center;
  background: #f7f9fa;
}

.developer__text h2 {
  font-size: 1.75rem;
  margin: 0 0 16px;
}

.developer__text p {
  color: #637282;
  line-height: 1.7;
  margin: 0;
}

.developer__code pre {
  margin: 0;
  padding: 24px;
  background: #1e1919;
  border-radius: 12px;
  overflow-x: auto;
  font-size: 0.85rem;
  line-height: 1.6;
}

.developer__code code {
  color: #e8ecf0;
  font-family: 'SF Mono', 'Fira Code', monospace;
}

.code-comment { color: #637282; }
.code-keyword { color: #7bacd6; }
.code-string { color: #a8d4a0; }

.faq {
  max-width: 720px;
  margin: 0 auto;
  padding: 80px 24px;
}

.faq__list {
  display: grid;
  gap: 12px;
}

.faq__item {
  border: 1px solid #e8ecf0;
  border-radius: 12px;
  padding: 4px 20px;
}

.faq__item summary {
  font-weight: 600;
  cursor: pointer;
  padding: 16px 0;
  list-style: none;
}

.faq__item summary::-webkit-details-marker {
  display: none;
}

.faq__item p {
  color: #637282;
  line-height: 1.7;
  margin: 0 0 16px;
  padding-top: 0;
}

.cta {
  text-align: center;
  padding: 80px 24px;
  background: #f7f9fa;
}

.cta h2 {
  font-size: 1.75rem;
  margin: 0 0 12px;
}

.cta p {
  color: #637282;
  margin: 0 0 32px;
}

.cta__actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.cta__actions .btn--text {
  display: inline-flex;
  align-items: center;
  text-decoration: underline;
}

@media (max-width: 900px) {
  .hero, .story, .developer {
    grid-template-columns: 1fr;
  }
  .capabilities__grid {
    grid-template-columns: 1fr;
  }
  .workflow__steps, .industries__grid, .integration__grid {
    grid-template-columns: 1fr;
  }
  .stats {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
